import { ParseNode, PromptFunction, ASTNode, LiteralNode } from '../types';
import { ParseError } from './ParseError';

/**
 * 提示词模板解析器
 * 负责将模板字符串解析为语法树
 */
type ControlToken = {
  kind: 'control';
  keyword: '#else' | '#endif';
  index: number;
};

export class PromptParser {
  private pos: number = 0;
  private input: string = '';
  private knownFunctionNames: Set<string> = new Set();
  private functionMetaByName: Record<string, PromptFunction> = {};

  constructor(knownFunctions?: string[] | Record<string, PromptFunction>) {
    if (!knownFunctions) return;
    if (Array.isArray(knownFunctions)) {
      this.knownFunctionNames = new Set(knownFunctions);
      return;
    }
    // record provided: capture both names and metadata
    this.functionMetaByName = knownFunctions;
    this.knownFunctionNames = new Set(Object.keys(knownFunctions));
  }

  setKnownFunctions(knownFunctions: string[] | Record<string, PromptFunction>) {
    if (Array.isArray(knownFunctions)) {
      this.functionMetaByName = {};
      this.knownFunctionNames = new Set(knownFunctions);
      return;
    }
    this.functionMetaByName = knownFunctions;
    this.knownFunctionNames = new Set(Object.keys(knownFunctions));
  }

  parse(input: string): ParseNode[] {
    this.input = input;
    this.pos = 0;
    return this.parseNodes();
  }

  private computeLineCol(index: number): { line: number; column: number } {
    let line = 1;
    let col = 1;
    for (let i = 0; i < index && i < this.input.length; i++) {
      if (this.input[i] === '\n') {
        line++;
        col = 1;
      } else {
        col++;
      }
    }
    return { line, column: col };
  }

  private isControlToken(node: any): node is ControlToken {
    return node && typeof node === 'object' && node.kind === 'control';
  }

  private parseNodes(): ParseNode[] {
    const nodes: ParseNode[] = [];
    while (this.pos < this.input.length) {
      const node = this.parseNode();
      if (!node) continue;
      if (this.isControlToken(node)) {
        const { line, column } = this.computeLineCol(node.index);
        throw new ParseError(`Unexpected ${node.keyword}`, line, column);
      }
      nodes.push(node);
    }
    return nodes;
  }

  private parseNode(): ParseNode | ControlToken | null {
    const char = this.input[this.pos];
    if (char === '{') {
      return this.parseExpression();
    } else {
      return this.parseText();
    }
  }

  private parseText(): ParseNode {
    let content = '';
    while (this.pos < this.input.length && this.input[this.pos] !== '{') {
      content += this.input[this.pos];
      this.pos++;
    }
    return { type: 'text', content };
  }

  private parseExpression(): ParseNode | ControlToken | null {
    if (this.input[this.pos] !== '{') return null;

    const startIndex = this.pos; // 记录起始位置用于报错
    this.pos++; // 跳过 '{'
    // 注释语法：{! ... !} 支持多行，直到遇到 !}
    if (this.input[this.pos] === '!') {
      this.pos++; // 跳过 '!'
      while (this.pos < this.input.length) {
        if (this.input[this.pos] === '!' && this.input[this.pos + 1] === '}') {
          this.pos += 2; // 消费 '!}'

          // 跳过紧跟着的换行符
          if (this.input[this.pos] === '\n') {
            this.pos++;
          }
          return null; // 注释完全被忽略，不产生任何节点
        }
        this.pos++;
      }
      const { line, column } = this.computeLineCol(startIndex);
      throw new ParseError('Unterminated comment, expected !}', line, column);
    }
    let content = '';
    let braceCount = 1;

    while (this.pos < this.input.length && braceCount > 0) {
      const char = this.input[this.pos];
      if (char === '{') braceCount++;
      else if (char === '}') braceCount--;

      if (braceCount > 0) content += char;
      this.pos++;
    }

    // 未闭合：抛出错误
    if (braceCount !== 0) {
      const { line, column } = this.computeLineCol(startIndex);
      throw new ParseError('Expected comment, end of input, expression, logic block, or regular text but "{" found.', line, column);
    }

    const inner = content.trim();
    // 空表达式报错（与用户期望一致）
    if (inner.length === 0) {
      const { line, column } = this.computeLineCol(startIndex);
      throw new ParseError('Expected comment, end of input, expression, logic block, or regular text but "{" found.', line, column);
    }

    if (inner.startsWith('#if ')) {
      const condition = inner.substring(4).trim();
      return this.parseConditionBlock(condition, startIndex);
    } else if (inner === '#else' || inner === '#endif') {
      return {
        kind: 'control',
        keyword: inner as '#else' | '#endif',
        index: startIndex
      };
    } else {
      // 普通表达式中校验函数名与参数
      this.validateFunctionCalls(inner, startIndex + 1);
      // 解析表达式为AST
      try {
        const ast = this.parseExpressionToAST(inner);
        return { type: 'expression', content: inner, ast };
      } catch (error) {
        // 如果解析失败，尝试提供更准确的错误位置
        if (error instanceof Error && error.message.includes('Unterminated function call')) {
          const { line, column } = this.computeLineCol(startIndex);
          throw new ParseError(error.message, line, column);
        }
        throw error;
      }
    }
  }

  private parseConditionBlock(condition: string, ifStartIndex: number): ParseNode {
    const normalizedCondition = this.normalizeConditionExpression(condition);
    // 在条件表达式中校验函数名
    this.validateFunctionCalls(normalizedCondition, ifStartIndex + 1);
    const conditionAST = this.parseExpressionToAST(normalizedCondition);

    const node: ParseNode = {
      type: 'condition',
      raw: condition,
      conditionAST,
      children: [],
      elseChildren: []
    };

    let parsingElse = false;

    while (this.pos < this.input.length) {
      const nextNode = this.parseNode();
      if (!nextNode) break;

      if (this.isControlToken(nextNode)) {
        if (nextNode.keyword === '#else') {
          if (parsingElse) {
            const { line, column } = this.computeLineCol(nextNode.index);
            throw new ParseError('Unexpected #else', line, column);
          }
          parsingElse = true;
          continue;
        }
        if (nextNode.keyword === '#endif') {
          return node;
        }
        continue;
      }

      if (parsingElse) node.elseChildren!.push(nextNode as ParseNode);
      else node.children!.push(nextNode as ParseNode);
    }

    // 未遇到 #endif
    const { line, column } = this.computeLineCol(ifStartIndex);
    throw new ParseError('Unterminated #if block, missing #endif', line, column);
  }

  private validateFunctionCalls(segment: string, segmentGlobalStartIndex: number) {
    if (!this.knownFunctionNames || this.knownFunctionNames.size === 0) return;

    // 预计算字符串区间，避免误判
    const stringSpans: Array<{ from: number; to: number }> = [];
    {
      let i = 0;
      while (i < segment.length) {
        const ch = segment[i];
        if (ch === '"' || ch === '\'') {
          const quote = ch;
          const start = i;
          i++;
          while (i < segment.length) {
            if (segment[i] === '\\') { i += 2; continue; }
            if (segment[i] === quote) { i++; break; }
            i++;
          }
          const end = i;
          stringSpans.push({ from: start, to: end });
          continue;
        }
        i++;
      }
    }

    const isInsideString = (idx: number) => stringSpans.some(s => idx >= s.from && idx < s.to);

    // 识别标识符（含点路径），将整个标识符视为函数名
    const identifierRegex = /\b([A-Za-z_][\w.]*)\b/g;
    let match: RegExpExecArray | null;
    while ((match = identifierRegex.exec(segment)) !== null) {
      const fullIdent = match[1];
      const startIdx = match.index;
      const endIdx = startIdx + match[0].length;

      if (isInsideString(startIdx)) continue; // 跳过字符串内
      if (fullIdent === 'if' || fullIdent === 'else' || fullIdent === 'endif') continue;
      // 检查是否是调用形式：标识符后跟可选空白再跟左括号
      let j = endIdx;
      while (j < segment.length && /\s/.test(segment[j])) j++;
      const isCall = segment[j] === '(';
      if (!this.knownFunctionNames.has(fullIdent)) {
        const absoluteIndex = segmentGlobalStartIndex + startIdx;
        const { line, column } = this.computeLineCol(absoluteIndex);
        throw new ParseError(`Unknown function: ${fullIdent}`, line, column);
      }
      if (!isCall) {
        // 作为无参调用进行校验：若存在必填参数则报错
        const meta = this.functionMetaByName[fullIdent];
        if (meta && meta.params && Array.isArray(meta.params)) {
          const requiredCount = meta.params.reduce((c, p) => p.required ? c + 1 : c, 0);
          if (requiredCount > 0) {
            const absoluteIndex = segmentGlobalStartIndex + startIdx;
            const { line, column } = this.computeLineCol(absoluteIndex);
            throw new ParseError(`Function ${fullIdent} requires ${requiredCount} argument(s)`, line, column);
          }
        }
        continue;
      }

      // 参数与必填类型校验（仅能基于字面量做静态推断）
      const argsSliceStart = j + 1; // skip '('
      const argsString = this.extractArgsString(segment, argsSliceStart);
      if (argsString == null) {
        const absoluteIndex = segmentGlobalStartIndex + j;
        const { line, column } = this.computeLineCol(absoluteIndex);
        throw new ParseError('Unterminated function call, missing ")"', line, column);
      }

      // 基于 metadata 做数量与类型约束
      const meta = this.functionMetaByName[fullIdent];
      if (meta && meta.params && Array.isArray(meta.params)) {
        const providedArgs = this.safeSplitArgs(argsString);
        const requiredCount = meta.params.reduce((c, p) => p.required ? c + 1 : c, 0);
        if (providedArgs.length < requiredCount) {
          const absoluteIndex = segmentGlobalStartIndex + startIdx;
          const { line, column } = this.computeLineCol(absoluteIndex);
          throw new ParseError(`Function ${fullIdent} requires ${requiredCount} argument(s)`, line, column);
        }
        if (providedArgs.length > meta.params.length) {
          const absoluteIndex = segmentGlobalStartIndex + startIdx;
          const { line, column } = this.computeLineCol(absoluteIndex);
          throw new ParseError(`Function ${fullIdent} expects at most ${meta.params.length} argument(s)`, line, column);
        }

        // 类型校验：能静态判断的再判断
        for (let idx = 0; idx < providedArgs.length && idx < meta.params.length; idx++) {
          const expected = meta.params[idx];
          if (!expected) continue;
          const actualType = this.inferLiteralType(providedArgs[idx]);
          if (actualType === 'unknown') continue; // 无法静态判断的跳过
          if (actualType !== expected.type) {
            const absoluteIndex = segmentGlobalStartIndex + startIdx;
            const { line, column } = this.computeLineCol(absoluteIndex);
            throw new ParseError(`Type mismatch for argument ${idx + 1} of ${fullIdent}: expected ${expected.type}, got ${actualType}`, line, column);
          }
        }
      }
    }
  }

  // 在 segment 中，从给定位置开始提取括号内的参数字符串（不包含外围括号）
  private extractArgsString(segment: string, fromIndex: number): string | null {
    let depth = 1;
    let inQuotes = false;
    let quoteChar = '';
    let i = fromIndex;
    let result = '';
    while (i < segment.length) {
      const ch = segment[i];
      if (inQuotes) {
        result += ch;
        if (ch === '\\') {
          // skip escaped next
          i += 2;
          continue;
        }
        if (ch === quoteChar) {
          inQuotes = false;
        }
        i++;
        continue;
      }
      if (ch === '"' || ch === '\'') {
        inQuotes = true;
        quoteChar = ch;
        result += ch;
        i++;
        continue;
      }
      if (ch === '(') {
        depth++;
        result += ch;
        i++;
        continue;
      }
      if (ch === ')') {
        depth--;
        if (depth === 0) {
          return result;
        }
        result += ch;
        i++;
        continue;
      }
      result += ch;
      i++;
    }
    return null;
  }

  // 将参数字符串在顶层逗号处分割，保留字面文本
  private safeSplitArgs(args: string): string[] {
    const parts: string[] = [];
    let current = '';
    let inQuotes = false;
    let quoteChar = '';
    let depth = 0;
    for (let i = 0; i < args.length; i++) {
      const ch = args[i];
      if (inQuotes) {
        current += ch;
        if (ch === '\\') {
          i++;
          if (i < args.length) current += args[i];
          continue;
        }
        if (ch === quoteChar) inQuotes = false;
        continue;
      }
      if (ch === '"' || ch === '\'') {
        inQuotes = true;
        quoteChar = ch;
        current += ch;
        continue;
      }
      if (ch === '(') { depth++; current += ch; continue; }
      if (ch === ')') { depth = Math.max(0, depth - 1); current += ch; continue; }
      if (ch === ',' && depth === 0) {
        parts.push(current.trim());
        current = '';
        continue;
      }
      current += ch;
    }
    if (current.trim()) parts.push(current.trim());
    return parts;
  }

  private inferLiteralType(rawArg: string): 'string' | 'number' | 'boolean' | 'unknown' {
    const s = rawArg.trim();
    if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith('\'') && s.endsWith('\''))) return 'string';
    if (/^[-+]?\d+(?:\.\d+)?$/.test(s)) return 'number';
    if (s === 'true' || s === 'false') return 'boolean';
    return 'unknown';
  }

  private normalizeConditionExpression(condition: string): string {
    const trimmed = condition.trim();
    const isMatch = this.findTopLevelIsOperator(trimmed);
    if (isMatch) {
      const left = this.normalizeConditionExpression(isMatch.left);
      const right = this.normalizeConditionExpression(isMatch.right);
      return `is(${left}, ${right})`;
    }
    return trimmed;
  }

  private findTopLevelIsOperator(expression: string): { left: string; right: string } | null {
    let depth = 0;
    let inQuotes = false;
    let quoteChar = '';

    for (let i = 0; i < expression.length; i++) {
      const char = expression[i];

      if (!inQuotes && (char === '"' || char === '\'')) {
        inQuotes = true;
        quoteChar = char;
        continue;
      }

      if (inQuotes && char === quoteChar) {
        if (i > 0 && expression[i - 1] === '\\') {
          continue;
        }
        inQuotes = false;
        continue;
      }

      if (inQuotes) continue;

      if (char === '(') {
        depth++;
        continue;
      }

      if (char === ')') {
        depth = Math.max(0, depth - 1);
        continue;
      }

      if (depth === 0) {
        if (
          expression.substring(i, i + 2) === 'is' &&
          this.isIsBoundaryChar(expression[i - 1]) &&
          this.isIsBoundaryChar(expression[i + 2])
        ) {
          const left = expression.substring(0, i).trim();
          const right = expression.substring(i + 2).trim();
          if (left && right) {
            return { left, right };
          }
        }
      }
    }

    return null;
  }

  private isIsBoundaryChar(char: string | undefined): boolean {
    if (char === undefined) return true;
    return /\s|[(),]/.test(char);
  }

  /**
   * 解析表达式为AST
   * @param expression 表达式字符串
   * @returns AST节点
   */
  private parseExpressionToAST(expression: string): ASTNode {
    const trimmed = expression.trim();

    // 空表达式不应该被解析为函数调用
    if (!trimmed) {
      throw new Error('Empty expression cannot be parsed');
    }

    // 检查是否是字面量
    if (this.isLiteral(trimmed)) {
      return this.parseLiteral(trimmed);
    }

    // 检查是否包含 is 操作符
    const isMatch = this.findTopLevelIsOperator(trimmed);
    if (isMatch) {
      const left = this.parseExpressionToAST(isMatch.left);
      const right = this.parseExpressionToAST(isMatch.right);
      return {
        type: 'functionCall',
        name: 'is',
        args: [left, right]
      };
    }

    // 检查是否是函数调用：使用括号匹配而不是正则表达式，以正确处理嵌套函数调用
    const funcNameMatch = trimmed.match(/^([\w.]+)\s*\(/);
    if (funcNameMatch) {
      const funcName = funcNameMatch[1];
      // extractArgsString 期望 fromIndex 指向 '(' 之后的位置（因为 depth 初始化为 1 表示已看到 '('）
      const argsStartIndex = funcNameMatch[0].length; // 指向 '(' 之后的位置
      const argsString = this.extractArgsString(trimmed, argsStartIndex);

      if (argsString !== null) {
        const args = this.parseArgumentsToAST(argsString);
        return {
          type: 'functionCall',
          name: funcName,
          args
        };
      }
      // 如果提取参数字符串失败，说明括号未闭合，抛出错误
      throw new Error(`Unterminated function call: ${funcName}, missing \")\"`);
    }

    // 否则作为无参函数调用处理
    return {
      type: 'functionCall',
      name: trimmed,
      args: []
    };
  }

  /**
   * 解析函数参数为AST节点数组
   * @param argsString 参数字符串
   * @returns AST节点数组
   */
  private parseArgumentsToAST(argsString: string): ASTNode[] {
    if (!argsString.trim()) return [];

    const args: ASTNode[] = [];
    let current = '';
    let inQuotes = false;
    let quoteChar = '';
    let parenCount = 0;

    for (let i = 0; i < argsString.length; i++) {
      const char = argsString[i];

      if (!inQuotes && (char === '"' || char === "'")) {
        inQuotes = true;
        quoteChar = char;
        current += char; // 包含开始引号
        continue;
      }

      if (inQuotes && char === quoteChar) {
        inQuotes = false;
        current += char; // 包含结束引号
        // 只有当字符串字面量是顶层参数时（parenCount == 0），才立即解析
        // 否则可能是嵌套在函数调用中的参数，应该继续累积
        if (parenCount === 0) {
          const trimmedLiteral = current.trim();
          if (trimmedLiteral) {
            args.push(this.parseLiteral(trimmedLiteral));
          }
          current = '';
        }
        continue;
      }

      if (!inQuotes) {
        if (char === '(') parenCount++;
        else if (char === ')') parenCount--;
        else if (char === ',' && parenCount === 0) {
          // 只有当参数不为空时才解析
          const trimmedCurrent = current.trim();
          if (trimmedCurrent) {
            args.push(this.parseExpressionToAST(trimmedCurrent));
          }
          current = '';
          continue;
        }
      }

      current += char;
    }

    if (current.trim()) {
      args.push(this.parseExpressionToAST(current.trim()));
    }

    return args;
  }

  /**
   * 检查字符串是否是字面量
   * @param str 字符串
   * @returns 是否是字面量
   */
  private isLiteral(str: string): boolean {
    const trimmed = str.trim();
    return (
      (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
      (trimmed.startsWith("'") && trimmed.endsWith("'")) ||
      /^[-+]?\d+(?:\.\d+)?$/.test(trimmed) ||
      trimmed === 'true' ||
      trimmed === 'false'
    );
  }

  /**
   * 解析字面量为AST节点
   * @param literal 字面量字符串
   * @returns 字面量AST节点
   */
  private parseLiteral(literal: string): LiteralNode {
    const trimmed = literal.trim();

    if ((trimmed.startsWith('"') && trimmed.endsWith('"')) ||
      (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
      return {
        type: 'literal',
        value: trimmed.slice(1, -1), // 移除引号
        valueType: 'string'
      };
    }

    if (/^\d+$/.test(trimmed)) {
      return {
        type: 'literal',
        value: parseInt(trimmed, 10),
        valueType: 'number'
      };
    }

    if (/^\d+\.\d+$/.test(trimmed)) {
      return {
        type: 'literal',
        value: parseFloat(trimmed),
        valueType: 'number'
      };
    }

    if (trimmed === 'true') {
      return {
        type: 'literal',
        value: true,
        valueType: 'boolean'
      };
    }

    if (trimmed === 'false') {
      return {
        type: 'literal',
        value: false,
        valueType: 'boolean'
      };
    }

    // 默认作为字符串字面量处理
    return {
      type: 'literal',
      value: trimmed,
      valueType: 'string'
    };
  }

  /**
   * 分析模板中指定函数的所有调用参数
   * @param template 模板字符串
   * @param functionName 要分析的函数名
   * @returns 二维数组，每个子数组包含一次函数调用的参数列表
   */
  static analyzeTemplate(template: string, functionName: string): string[][] {
    const parser = new PromptParser();
    const nodes = parser.parse(template);
    const functionCalls: string[][] = [];

    const extractFunctionCalls = (nodeList: ParseNode[]) => {
      for (const node of nodeList) {
        if (node.type === 'expression') {
          const calls = extractCallsFromExpression(node.content, functionName);
          functionCalls.push(...calls);
        } else if (node.type === 'condition') {
          const calls = extractCallsFromExpression(node.raw, functionName);
          functionCalls.push(...calls);
          extractFunctionCalls(node.children);
          if (node.elseChildren) extractFunctionCalls(node.elseChildren);
        }
      }
    };

    extractFunctionCalls(nodes);
    return functionCalls;
  }
}

/**
 * 从表达式中提取指定函数的调用参数
 */
function extractCallsFromExpression(expression: string, functionName: string): string[][] {
  const calls: string[][] = [];
  const functionRegex = new RegExp(`\\b${functionName}\\s*\\(`, 'g');
  let match: RegExpExecArray | null;

  while ((match = functionRegex.exec(expression)) !== null) {
    const startIndex = match.index + match[0].length - 1;
    const argsString = extractArgsString(expression, startIndex);

    if (argsString !== null) {
      const args = safeSplitArgs(argsString);
      calls.push(args);
    }
  }

  return calls;
}

/**
 * 提取函数调用中的参数字符串（复用 PromptParser 的逻辑）
 */
function extractArgsString(expression: string, startIndex: number): string | null {
  let depth = 1;
  let inQuotes = false;
  let quoteChar = '';
  let i = startIndex + 1;
  let result = '';

  while (i < expression.length) {
    const ch = expression[i];

    if (inQuotes) {
      result += ch;
      if (ch === '\\') {
        i += 2;
        continue;
      }
      if (ch === quoteChar) inQuotes = false;
      i++;
      continue;
    }

    if (ch === '"' || ch === '\'') {
      inQuotes = true;
      quoteChar = ch;
      result += ch;
      i++;
      continue;
    }

    if (ch === '(') {
      depth++;
      result += ch;
      i++;
      continue;
    }

    if (ch === ')') {
      depth--;
      if (depth === 0) return result;
      result += ch;
      i++;
      continue;
    }

    result += ch;
    i++;
  }

  return null;
}

/**
 * 安全地分割参数字符串（复用 PromptParser 的逻辑）
 */
function safeSplitArgs(args: string): string[] {
  const parts: string[] = [];
  let current = '';
  let inQuotes = false;
  let quoteChar = '';
  let depth = 0;

  for (let i = 0; i < args.length; i++) {
    const ch = args[i];

    if (inQuotes) {
      current += ch;
      if (ch === '\\') {
        i++;
        if (i < args.length) current += args[i];
        continue;
      }
      if (ch === quoteChar) inQuotes = false;
      continue;
    }

    if (ch === '"' || ch === '\'') {
      inQuotes = true;
      quoteChar = ch;
      current += ch;
      continue;
    }

    if (ch === '(') {
      depth++;
      current += ch;
      continue;
    }
    if (ch === ')') {
      depth = Math.max(0, depth - 1);
      current += ch;
      continue;
    }
    if (ch === ',' && depth === 0) {
      parts.push(current.trim());
      current = '';
      continue;
    }

    current += ch;
  }

  if (current.trim()) parts.push(current.trim());
  return parts;
} 